import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";
import type {LinkElement} from "slate";
import {useSlate, type RenderElementProps} from "slate-react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {styles as wbStyles} from "@khanacademy/wonder-blocks-typography";

import {unwrapLinkNode, updateLinkNode} from "../helpers/link-helpers.ts";
import {isSelectionWithinNode} from "../helpers/node-helpers.ts";

import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";
import unlinkIcon from "@phosphor-icons/core/regular/link-break.svg";

const StyledA = addStyle("a", wbStyles.Body);

export default function LinkNode(
    props: RenderElementProps,
): React.ReactElement {
    const {element, attributes, children} = props;

    const link = element as LinkElement;
    const editor = useSlate();
    const [url, setUrl] = useState(link.url || "");
    const isSelectionWithin = isSelectionWithinNode(editor, element);

    useEffect(() => {
        if (url) {
            updateLinkNode(editor, url);
        } else {
            unwrapLinkNode(editor);
        }
    }, [editor, url]);

    return (
        <Popover
            dismissEnabled
            opened={isSelectionWithin}
            content={
                <PopoverContentCore style={styles.popover}>
                    <TextField
                        id="coeditor-link-url-editor"
                        value={url}
                        onChange={setUrl}
                        style={styles.url}
                        placeholder={t`Link URL`}
                        autoFocus
                        autoComplete="off"
                    />
                    <IconButton
                        icon={externalLinkIcon}
                        kind="tertiary"
                        actionType="neutral"
                        style={styles.button}
                        target="_blank"
                        href={url}
                        aria-label={t`Visit link`}
                    />
                    <IconButton
                        icon={unlinkIcon}
                        actionType="destructive"
                        kind="tertiary"
                        style={styles.button}
                        onClick={() => unwrapLinkNode(editor)}
                        aria-label={t`Unlink text`}
                    />
                </PopoverContentCore>
            }
        >
            <StyledA
                {...attributes}
                style={styles.a}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </StyledA>
        </Popover>
    );
}

const styles = StyleSheet.create({
    a: {
        color: color.blue,
        display: "inline",
        fontSize: "inherit",
        fontWeight: "inherit",
    },
    popover: {
        display: "flex",
        flexDirection: "row",
        padding: spacing.xSmall_8,
    },
    url: {
        paddingInlineStart: spacing.xSmall_8,
    },
    button: {
        flex: 0,
        width: spacing.xLarge_32,
        padding: spacing.xxxSmall_4,
        marginInlineStart: spacing.xxxSmall_4,
        border: 0,
    },
});
